import { useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import CommunityMembersComponent from './CommunityMembersComponent';

import { getCommunityMembers } from '../actions';

import * as selectors from '../../Navigation/selectors';
import * as commonSelectors from '../../Shared/selectors';
import * as communitySelectors from '../selectors';

import usePrevious from '../../../hooks/usePrevious';
import CommunityContext from '../context';

const CommunityMembersContainer = (props) => {
  const history = useHistory();
  const checkIfCommunityMember = useContext(CommunityContext);
  const {
    isLoading,
    isMobileScreen,
    getCommunityMembers,
    communityId,
    communityMembersList,
    nextCommunityMembers,
    communityMembersCount,
    hasNewNotifications,
  } = props;

  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showCommunityMembers, setShowCommunityMembers] = useState(
    !isMobileScreen,
  );

  const queryParams = {
    community: communityId,
    q: search,
  };

  const prevSearch = usePrevious(search);
  const onSearch = useCallback(
    debounce((queryParams) => getCommunityMembers(queryParams, true), 800),
    [],
  );

  useEffect(() => {
    getCommunityMembers(queryParams, true);
  }, [communityId]);

  useEffect(() => {
    setShowCommunityMembers(!isMobileScreen);
  }, [isMobileScreen]);

  useEffect(() => {
    if (!prevSearch && !search) return;
    onSearch(queryParams);
  }, [search]);

  const onMessagesClick = () => {
    if (!checkIfCommunityMember()) return;
    history.push(`/community/messages`);
  };

  const loadMore = async () => {
    if (!nextCommunityMembers) return;
    setIsLoadingMore(true);
    await getCommunityMembers({ ...queryParams, ...nextCommunityMembers });
    setIsLoadingMore(false);
  };

  return (
    <CommunityMembersComponent
      search={search}
      setSearch={setSearch}
      loadMore={loadMore}
      communityMembersList={communityMembersList}
      nextCommunityMembers={nextCommunityMembers}
      communityMembersCount={communityMembersCount}
      isMobileScreen={isMobileScreen}
      showCommunityMembers={showCommunityMembers}
      setShowCommunityMembers={setShowCommunityMembers}
      isLoading={isLoading}
      isLoadingMore={isLoadingMore}
      onMessagesClick={onMessagesClick}
      hasNewNotifications={hasNewNotifications}
      isModalOpen={isModalOpen}
      setIsModalOpen={setIsModalOpen}
      communityId={communityId}
    />
  );
};

CommunityMembersContainer.defaultProps = {
  nextCommunityMembers: null,
  communityMembersList: [],
};

CommunityMembersContainer.propTypes = {
  communityMembersList: PropTypes.instanceOf(Array),
  nextCommunityMembers: PropTypes.instanceOf(Object),
  getCommunityMembers: PropTypes.func.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
  communityId: PropTypes.number.isRequired,
  communityMembersCount: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  hasNewNotifications: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => {
  const communityMembers = communitySelectors.getCommunityMembers(state);
  const isMobileScreen = commonSelectors.getIsMobileScreen(state);
  const isLoading = commonSelectors.getIsLoading(state);
  const hasNewNotifications =
    selectors.getHasNewCommunityThreadsWithUpdates(state);
  return {
    communityMembersList: communityMembers?.results,
    nextCommunityMembers: communityMembers?.next,
    communityMembersCount: communityMembers?.count,
    isMobileScreen,
    isLoading,
    hasNewNotifications,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getCommunityMembers: (queryParams, isClearPrev) =>
    getCommunityMembers(dispatch, queryParams, isClearPrev),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CommunityMembersContainer);
